import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router'
import { useStateWithCallbackLazy } from 'use-state-with-callback'
import { useExameMausTratosService } from 'hooks/useExameMausTratosService'
import { useExameRealizadoService } from 'hooks/useExameRealizadoService'
import { useArquivoService } from 'hooks/useArquivoService'
import useRouter from 'utils/useRouter'
import { validarData } from 'utils/helpers'

export const useExameMausTratos = () => {
  const params = useParams()
  const router = useRouter()

  const exameMausTratosService = useExameMausTratosService()
  const exameRealizadoService = useExameRealizadoService()
  const arquivoService = useArquivoService()

  const [exameMaustratos, setExameMaustratos] = useState(null)
  const [lesoes, setLesoes] = useStateWithCallbackLazy([])
  const [svgElements, setSvgElements] = useState({})
  
  const [processando, setProcessando] = useState(false)
  const [processandoOperacao, setProcessandoOperacao] = useState(false)

  const [formState, setFormState] = useState({
    autoDeclaracao: '',
    motivoExame: '',
    localOcorrencia: '',
    dataOcorrencia: '',
    dataOcorrenciaFormatada: null,
    dataOcorrenciaValida: false,
    horaOcorrencia: '',
    instrumentoOfensa: null,
    nomeAgressor: '',
    numeroGolpes: '',
    ataqueSurpresa: 'false',
    contencaoVitima: 'false',
    quemConteve: '',
    versaoPericiando: '',
    descricaoDor: '',
    dataRealizacaoExame: '',
    dataRealizacaoExameFormatada: null,
    dataRealizacaoExameValida: false,
    horaInicioExame: '',
    horaTerminoExame: '',
    localExame: '',
    acompanhantePericiando: '',
    pessoasPresentesExame: '',
    consentimentoEsclarecido: 'false',
    motivoSemConsentimentoEsclarecido: '',
    necessarioContencao: 'false',
    motivoContencao: '',
    exameIndireto: '',
    conclusao: '',
  })

  const fetchExameMausTratos = useCallback(async () => {
    try {
      setProcessando(true)
      const result = await exameMausTratosService.listarExameMausTratosDetalhadoAsync(params.idExameRealizado)
      setExameMaustratos(result)
      setLesoes(result.exReal.lesoes, () => {
        setProcessando(false)
      })
    } catch (err) {
      router.history.push('/dashboard')
    }
  }, [params.idExameRealizado, router])

  useEffect(() => {
    fetchExameMausTratos()
  }, [fetchExameMausTratos])

  useEffect(() => {
    if (exameMaustratos?.id) {
      const formUpdate = {
        autoDeclaracao: exameMaustratos.autoDeclaracao || '',
        motivoExame: exameMaustratos.motivoExame || '',
        localOcorrencia: exameMaustratos.localOcorrencia || '',
        horaOcorrencia: exameMaustratos.horaOcorrencia || '',
        instrumentoOfensa: exameMaustratos.instrumentoOfensa || null,
        nomeAgressor: exameMaustratos.nomeAgressor || '',
        numeroGolpes: exameMaustratos.numeroGolpes || '',
        ataqueSurpresa: exameMaustratos.ataqueSurpresa ? 'true' : 'false',
        contencaoVitima: exameMaustratos.contencaoVitima ? 'true' : 'false',
        quemConteve: exameMaustratos.quemConteve || '',
        versaoPericiando: exameMaustratos.versaoPericiando || '',
        descricaoDor: exameMaustratos.descricaoDor || '',
        horaInicioExame: exameMaustratos.horaInicioExame || '',
        horaTerminoExame: exameMaustratos.horaTerminoExame || '',
        localExame: exameMaustratos.localExame || '',
        acompanhantePericiando: exameMaustratos.acompanhantePericiando || '',
        pessoasPresentesExame: exameMaustratos.pessoasPresenteExame || '',
        consentimentoEsclarecido: exameMaustratos.consentimentoEsclarecido ? 'true' : 'false',
        motivoSemConsentimentoEsclarecido: exameMaustratos.motivoSemConsentimentoEsclarecido || '',
        necessarioContencao: exameMaustratos.necessarioContencao ? 'true' : 'false',
        motivoContencao: exameMaustratos.motivoContencao || '',
        exameIndireto: exameMaustratos.exameIndireto || '',
        conclusao: exameMaustratos.conclusao || '',
        dataOcorrencia: '',
        dataOcorrenciaFormatada: null,
        dataOcorrenciaValida: false,
        dataRealizacaoExame: '',
        dataRealizacaoExameFormatada: null,
        dataRealizacaoExameValida: false,
      }

      if (exameMaustratos.dataOcorrencia) {
        const [ano, mes, dia] = exameMaustratos.dataOcorrencia.split('-')
        formUpdate.dataOcorrencia = `${dia}/${mes}/${ano}`
        formUpdate.dataOcorrenciaFormatada = exameMaustratos.dataOcorrencia
        formUpdate.dataOcorrenciaValida = true
      }

      if (exameMaustratos.dataRealizacaoExame) {
        const [ano, mes, dia] = exameMaustratos.dataRealizacaoExame.split('-')
        formUpdate.dataRealizacaoExame = `${dia}/${mes}/${ano}`
        formUpdate.dataRealizacaoExameFormatada = exameMaustratos.dataRealizacaoExame
        formUpdate.dataRealizacaoExameValida = true
      }

      setFormState(formUpdate)
    }
  }, [exameMaustratos])

  const handleChange = useCallback((e) => {
    const { name, value } = e.target
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }))
  }, [])

  const handleAutocompleteChange = useCallback((name, value) => {
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }))
  }, [])

  const handleChangeCampoData = useCallback((campo, valor) => {
    const splitted = valor.split('/')
    let [dia, mes, ano] = splitted

    if (dia && parseInt(dia) > 31) dia = '31'
    if (mes && parseInt(mes) > 12) mes = '12'

    const novoValorData = `${dia || ''}/${mes || ''}/${ano || ''}`

    if (campo === 'ocorrencia') {
      const valida = validarData(novoValorData)
      setFormState((prev) => ({
        ...prev,
        dataOcorrencia: novoValorData,
        dataOcorrenciaValida: valida,
        dataOcorrenciaFormatada: ano?.length === 4 && mes && dia ? `${ano}-${mes}-${dia}` : null,
      }))
    }

    if (campo === 'realizacaoExame') {
      const valida = validarData(novoValorData)
      setFormState((prev) => ({
        ...prev,
        dataRealizacaoExame: novoValorData,
        dataRealizacaoExameValida: valida,
        dataRealizacaoExameFormatada: ano?.length === 4 && mes && dia ? `${ano}-${mes}-${dia}` : null,
      }))
    }
  }, [])

  const handleConfirmar = useCallback(async () => {
    const dados = {
      autoDeclaracao: formState.autoDeclaracao,
      motivoExame: formState.motivoExame,
      localOcorrencia: formState.localOcorrencia,
      dataOcorrencia: formState.dataOcorrenciaFormatada,
      horaOcorrencia: formState.horaOcorrencia,
      instrumentoOfensa: formState.instrumentoOfensa,
      nomeAgressor: formState.nomeAgressor,
      motivoSemConsentimentoEsclarecido: formState.motivoSemConsentimentoEsclarecido,
      numeroGolpes: formState.numeroGolpes,
      ataqueSurpresa: formState.ataqueSurpresa === 'true',
      contencaoVitima: formState.contencaoVitima === 'true',
      quemConteve: formState.quemConteve,
      versaoPericiando: formState.versaoPericiando,
      descricaoDor: formState.descricaoDor,
      dataRealizacaoExame: formState.dataRealizacaoExameFormatada,
      horaInicioExame: formState.horaInicioExame,
      horaTerminoExame: formState.horaTerminoExame,
      localExame: formState.localExame,
      acompanhantePericiando: formState.acompanhantePericiando,
      pessoasPresentesExame: formState.pessoasPresentesExame,
      consentimentoEsclarecido: formState.consentimentoEsclarecido === 'true',
      necessarioContencao: formState.necessarioContencao === 'true',
      motivoContencao: formState.motivoContencao,
      exameIndireto: formState.exameIndireto,
      conclusao: formState.conclusao,
      lesoes,
    }

    setProcessandoOperacao(true)
    await exameMausTratosService.confirmarDadosExameMausTratos(params.idExameRealizado, dados, () => {})
    
    const responseSVGLesaoFront = await arquivoService.uploadSVGCalunga(svgElements.svgFront, 'svg_lesao_front')
    const responseSVGLesaoBack = await arquivoService.uploadSVGCalunga(svgElements.svgBack, 'svg_lesao_back')
    const responseSVGLesaoLeft = await arquivoService.uploadSVGCalunga(svgElements.svgLeft, 'svg_lesao_left')
    const responseSVGLesaoRight = await arquivoService.uploadSVGCalunga(svgElements.svgRight, 'svg_lesao_right')

    const dadosSvg = {
      id_svg_front: responseSVGLesaoFront.data.location,
      id_svg_back: responseSVGLesaoBack.data.location,
      id_svg_left: responseSVGLesaoLeft.data.location,
      id_svg_right: responseSVGLesaoRight.data.location,
    }

    await exameMausTratosService.editarExameMausTratos(params.idExameRealizado, dadosSvg, () => {})
    await exameRealizadoService.gerarDocumento(params.idExameRealizado, () => {})
    setProcessandoOperacao(false)

    router.history.push({
      pathname: `/casos/caso/solicitacao/exameSolicitado/editorExameRealizado/${params.idExameRealizado}`,
      state: { IgnoneUploadImagem: true },
    })
  }, [formState, lesoes, svgElements, params.idExameRealizado, router])

  return {
    params,
    exameMaustratos,
    lesoes,
    setLesoes,
    setSvgElements,
    processando,
    processandoOperacao,
    formState,
    handleChange,
    handleAutocompleteChange,
    handleChangeCampoData,
    handleConfirmar,
    router,
  }
}
